/**
 * Initialisation l'indicateur de mouvement sous les liens du menu
 *
 * @version 0.0.1 - 19/03/2026 by LM -
 */
const initMoveIndicator = (item: HTMLElement, navbar: HTMLElement, indicator: HTMLElement): void => {
    const label = item.querySelector('.menu-label')
    const rect = label.getBoundingClientRect()
    const navRect = navbar.getBoundingClientRect()

    const padding = 12

    indicator.style.width = rect.width + padding + 'px'
    indicator.style.left = (rect.left - navRect.left - padding / 2) + 'px'
    indicator.style.opacity = '1'
}

/**
 * Initialise le comportement des dropdown de la navbar (premier sous-menu de sous-menu actif par défaut + gestion de la bordure sur l'élément actif)
 *
 * @version 0.0.1 - 27/03/2026 by LM -
 */
const initDropdownComportment = (menu: HTMLElement): void => {
    menu.querySelectorAll('[id^="dropdown-"]').forEach(dropdown => {
        const submenus = dropdown.querySelectorAll<HTMLElement>('[id^="submenu-"]');
        const triggers = dropdown.querySelectorAll<HTMLElement>('[data-submenu-target]');
        const hideLinks = dropdown.querySelectorAll<HTMLElement>('[data-hide-submenus]');
        const panel = dropdown.querySelector<HTMLElement>('#nav-submenu-panel');

        if (!submenus.length && !triggers.length) return;

        const resetAll = () => {
            submenus.forEach(sub => sub.classList.add('hidden'));
            if (panel) panel.classList.add('hidden');
            triggers.forEach(btn => {
                btn.style.borderLeftColor = '';
            });
        };

        // Au survol d'un item avec sous-menu, afficher le panneau droit et le sous-menu ciblé.
        // La couleur du trait est forcée en inline style pour éviter que `border-l-transparent`
        // (défini après `border-l-primary-500` dans le CSS) ne l'écrase.
        triggers.forEach(trigger => {
            trigger.addEventListener('mouseenter', () => {
                resetAll();
                const targetId = trigger.dataset.submenuTarget;
                const target = document.getElementById(targetId);
                if (target) {
                    if (panel) panel.classList.remove('hidden');
                    target.classList.remove('hidden');
                    trigger.style.borderLeftColor = 'var(--color-primary-500)';
                }
            });
        });

        // Au survol d'un lien sans sous-menu, masquer le panneau droit
        hideLinks.forEach(link => {
            link.addEventListener('mouseenter', () => resetAll());
        });

        // Réinitialiser quand le dropdown se rouvre
        const observer = new MutationObserver(() => resetAll());
        observer.observe(dropdown, { attributes: true, attributeFilter: ['class'] });
    });
}

/**
 * Initialisation des éléments supplémentaire de la navbar
 *
 * @version 0.0.1 - 19/03/2026 by LM - Gestion de l'indicateur de mouvement sous les liens
 */
export const initNav = (): void => {
    const sentinel: HTMLElement = document.querySelector('[data-navigation="main-navigation-sentinel"]')
    const navbar: HTMLElement = document.querySelector('[data-navigation="main-navigation"]')

    const observer = new IntersectionObserver(([entry]) => {
        navbar.classList.toggle('is-stuck', !entry.isIntersecting);
    }, { threshold: 1 });

    observer.observe(sentinel);

    const menu: HTMLElement = document.querySelector('#main-menu')
    const indicator: HTMLElement = document.querySelector('#menu-indicator')
    const items: NodeListOf<HTMLElement> = menu.querySelectorAll('.menu-item')
    const dropdowns: NodeListOf<HTMLElement> = menu.querySelectorAll('[id^="dropdown-"]')

    // Pose la position d'un indicateur sous un item. `animate=false` = placement instantané
    // (on coupe la transition + reflow pour éviter le glissement depuis l'ancienne position).
    const place = (indicatorEl: HTMLElement, item: HTMLElement, animate: boolean): void => {
        if (animate) {
            initMoveIndicator(item, navbar, indicatorEl)
            return
        }
        const previousTransition = indicatorEl.style.transition
        indicatorEl.style.transition = 'none'
        initMoveIndicator(item, navbar, indicatorEl)
        void indicatorEl.offsetWidth
        indicatorEl.style.transition = previousTransition
    }

    // 2e repère : trait « ouvert » persistant, toujours sous l'entrée dont le dropdown est
    // ouvert. Détecté via la classe `hidden` du dropdown (Flowbite la retire à l'ouverture).
    const openIndicator = indicator.cloneNode(false) as HTMLElement
    openIndicator.id = 'menu-indicator-open'
    openIndicator.style.opacity = '0'
    indicator.parentElement?.appendChild(openIndicator)

    const syncOpenIndicator = (): void => {
        const openDropdown = Array.from(dropdowns).find(d => !d.classList.contains('hidden'))
        const trigger = openDropdown
            ? menu.querySelector<HTMLElement>(`.menu-item[data-dropdown-toggle="${openDropdown.id}"]`)
            : null
        if (trigger) {
            place(openIndicator, trigger, openIndicator.style.opacity === '1')
        } else {
            openIndicator.style.opacity = '0'
        }
    }
    dropdowns.forEach(d => new MutationObserver(syncOpenIndicator).observe(d, { attributes: true, attributeFilter: ['class'] }))

    // 1er repère : trait de survol. Au 1er hover on le pose sans transition (pas de glissement).
    let isMenuActive = false

    items.forEach(item => {
        item.addEventListener('mouseenter', () => {
            place(indicator, item, isMenuActive)
            isMenuActive = true
        })
    })

    menu.addEventListener('mouseleave', () => {
        indicator.style.opacity = '0'
        isMenuActive = false
    })

    // Quand on entre dans un dropdown (le panneau), le trait de SURVOL se masque : il ne reste
    // alors que le trait « ouvert » persistant sous l'entrée déroulée. Le survol d'une autre
    // entrée déplace le trait de survol sous elle, puis il disparaît au retour dans le dropdown.
    dropdowns.forEach(d => d.addEventListener('mouseenter', () => {
        indicator.style.opacity = '0'
    }))

    // Comportement des dropdown
    initDropdownComportment(menu);

    // Fermer le menu mobile au clic en dehors (simule un clic sur le burger)
    const mobileMenu = document.getElementById('mobile-menu')
    const burgerBtn = navbar.querySelector<HTMLButtonElement>('[data-collapse-toggle="mobile-menu"]')
    if (mobileMenu && burgerBtn) {
        document.addEventListener('click', (e: MouseEvent) => {
            const target = e.target as HTMLElement
            if (!navbar.contains(target) && !mobileMenu.classList.contains('hidden')) {
                burgerBtn.click()
            }
        })

        // Ouvrir les langues ferme le menu mobile : les deux panneaux sont en z-50, le tiroir recouvre.
        const langueBtn = navbar.querySelector<HTMLButtonElement>('[data-dropdown-toggle="language-selector-dropdown"]')
        langueBtn?.addEventListener('click', () => {
            if (!mobileMenu.classList.contains('hidden')) {
                burgerBtn.click()
            }
        })
    }
}
